
/**
 * Discount Code Tracker
 *
 * Important:
 * - utm_campaign is tracking only.
 * - utm_campaign is NEVER treated as coupon code.
 * - /coupon/yeahdeals always becomes YEAHDEALS.
 */

(function () {
  'use strict';

  if (window.__NEDiscountTrackerNoUtmCampaignV4) return;
  window.__NEDiscountTrackerNoUtmCampaignV4 = true;

  const STORAGE_KEY = 'ne_discount_code';
  const COOKIE_NAME = 'ne_discount_code';
  const DETAILS_STORAGE_KEY = 'ne_discount_details';
  const COOKIE_DAYS = 30;

  // Versioned because older cache entries could contain a campaign-wide
  // fallback percentage instead of confirmed per-product eligibility.
  const PRODUCT_DISCOUNT_CACHE_PREFIX = 'ne_discount_product_cache_v4_';
  const PRODUCT_DISCOUNT_CACHE_TTL = 30 * 60 * 1000;

  const BLOCKED_SOURCE_PATHS = [
    'collections',
    'products',
    'pages',
    'blogs',
    'cart',
    'checkout',
    'account',
    'search',
    'apps',
    'admin',
    'cdn',
    'policies',
    'tools',
    'a',
    'community'
  ];

  function getRootPath() {
    if (window.Shopify && Shopify.routes && Shopify.routes.root) {
      return Shopify.routes.root;
    }

    return '/';
  }

  function buildUrl(path) {
    const root = getRootPath();
    return root + String(path || '').replace(/^\/+/, '');
  }

  function normalizeCode(code) {
    return String(code || '').trim().toUpperCase();
  }

  function looksLikeDiscountCode(code) {
    code = String(code || '').trim();

    if (!code) return false;
    if (code.length < 2) return false;
    if (code.indexOf('.') !== -1) return false;
    if (code.indexOf('@') !== -1) return false;

    return /^[a-zA-Z0-9_-]+$/.test(code);
  }

  function isProbablyUtmCampaignCode(code) {
    code = normalizeCode(code);

    if (!code) return false;

    return (
      code.indexOf('BRAND_WEEK_') === 0 ||
      code.indexOf('MAINSALE') !== -1 ||
      code.indexOf('PAID_SOCIAL') !== -1 ||
      code.indexOf('CAMPAIGN') !== -1 ||
      code.indexOf('ADSET') !== -1
    );
  }

  function setCookie(name, value, days) {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);

    document.cookie =
      name +
      '=' +
      encodeURIComponent(value) +
      ';expires=' +
      date.toUTCString() +
      ';path=/;SameSite=Lax';
  }

  function getCookie(name) {
    const nameEQ = name + '=';
    const ca = document.cookie.split(';');

    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];

      while (c.charAt(0) === ' ') {
        c = c.substring(1, c.length);
      }

      if (c.indexOf(nameEQ) === 0) {
        return decodeURIComponent(c.substring(nameEQ.length, c.length));
      }
    }

    return null;
  }

  function deleteCookie(name) {
    document.cookie =
      name + '=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;SameSite=Lax';
  }

  function clearDiscountDetails() {
    try {
      sessionStorage.removeItem(DETAILS_STORAGE_KEY);
    } catch (error) {}
  }

  function clearProductDiscountCache() {
    try {
      Object.keys(sessionStorage).forEach(function (key) {
        if (key.indexOf(PRODUCT_DISCOUNT_CACHE_PREFIX) === 0) {
          sessionStorage.removeItem(key);
        }
      });
    } catch (error) {}

    if (window.NE && window.NE.discountProductMemoryCache) {
      window.NE.discountProductMemoryCache = {};
    }
  }

  function clearProductDiscountCacheForCode(code) {
    if (!code) return;

    code = normalizeCode(code);

    try {
      sessionStorage.removeItem(PRODUCT_DISCOUNT_CACHE_PREFIX + code);
    } catch (error) {}

    if (window.NE && window.NE.discountProductMemoryCache) {
      Object.keys(window.NE.discountProductMemoryCache).forEach(function (key) {
        if (key.indexOf(code + ':') === 0) {
          delete window.NE.discountProductMemoryCache[key];
        }
      });
    }
  }

  function saveDiscountCode(code) {
    code = normalizeCode(code);

    if (!code) return null;
    if (!looksLikeDiscountCode(code)) return null;

    try {
      const previousCode = normalizeCode(sessionStorage.getItem(STORAGE_KEY));

      sessionStorage.setItem(STORAGE_KEY, code);
      sessionStorage.setItem('ne_vanity_discount', code);
      sessionStorage.removeItem(DETAILS_STORAGE_KEY);

      setCookie(COOKIE_NAME, code, COOKIE_DAYS);

      if (previousCode && previousCode !== code) {
        clearDiscountDetails();
        clearProductDiscountCacheForCode(previousCode);
      }

      window.dispatchEvent(
        new CustomEvent('ne:discount-code-updated', {
          detail: { code: code }
        })
      );

      return code;
    } catch (error) {
      setCookie(COOKIE_NAME, code, COOKIE_DAYS);

      window.dispatchEvent(
        new CustomEvent('ne:discount-code-updated', {
          detail: { code: code }
        })
      );

      return code;
    }
  }

  function getDiscountCode() {
    let storedCode = '';

    try {
      storedCode = sessionStorage.getItem(STORAGE_KEY) || getCookie(COOKIE_NAME) || '';
    } catch (error) {
      storedCode = getCookie(COOKIE_NAME) || '';
    }

    storedCode = normalizeCode(storedCode);

    if (isProbablyUtmCampaignCode(storedCode)) {
      clearDiscountCode();
      return null;
    }

    return storedCode || null;
  }

  function clearDiscountCode() {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
      sessionStorage.removeItem('ne_vanity_discount');
      sessionStorage.removeItem(DETAILS_STORAGE_KEY);
      clearProductDiscountCache();
    } catch (error) {}

    deleteCookie(COOKIE_NAME);

    window.dispatchEvent(new CustomEvent('ne:discount-code-cleared'));
  }

  function isBlockedSource(part) {
    part = String(part || '').toLowerCase();
    return BLOCKED_SOURCE_PATHS.indexOf(part) !== -1;
  }

  function getCouponFromPath() {
    const pathname = window.location.pathname || '';
    const pathParts = pathname.split('/').filter(Boolean);

    /*
      Highest priority:
      /collections/yeah-deals/coupon/yeahdeals
      /coupon/yeahdeals
    */
    for (let i = 0; i < pathParts.length; i++) {
      const currentPart = String(pathParts[i] || '').toLowerCase();

      if (currentPart === 'coupon' && pathParts[i + 1]) {
        const couponCode = normalizeCode(decodeURIComponent(pathParts[i + 1]));

        if (looksLikeDiscountCode(couponCode)) {
          return couponCode;
        }
      }
    }

    /*
      Shopify discount route:
      /discount/YEAHDEALS
    */
    const discountPathMatch = pathname.match(/\/discount\/([^/?#]+)/i);

    if (discountPathMatch && discountPathMatch[1]) {
      const discountCode = normalizeCode(decodeURIComponent(discountPathMatch[1]));

      if (looksLikeDiscountCode(discountCode)) {
        return discountCode;
      }
    }

    return null;
  }

  function getCouponFromParams() {
    const urlParams = new URLSearchParams(window.location.search || '');

    /*
      Explicit coupon params only.
      Important:
      utm_campaign is NOT included.
    */
    const paramCode =
      urlParams.get('ne_coupon') ||
      urlParams.get('discount') ||
      urlParams.get('coupon') ||
      urlParams.get('code');

    const couponCode = normalizeCode(paramCode);

    if (looksLikeDiscountCode(couponCode)) {
      return couponCode;
    }

    return null;
  }

  function getCouponFromGenericSourcePath() {
    const pathname = window.location.pathname || '';
    const pathParts = pathname.split('/').filter(Boolean);

    /*
      Generic source URLs:
      /youtube/CODE
      /instagram/CODE
      /meta/CODE
      /crm/CODE
      /newsletter/CODE
      /collections/example/meta/CODE
    */
    if (pathParts.length >= 2) {
      const possibleSource = String(pathParts[pathParts.length - 2] || '').toLowerCase();
      const possibleCode = normalizeCode(decodeURIComponent(pathParts[pathParts.length - 1] || ''));

      if (
        possibleSource &&
        possibleCode &&
        !isBlockedSource(possibleSource) &&
        looksLikeDiscountCode(possibleCode)
      ) {
        return possibleCode;
      }
    }

    return null;
  }

  function detectDiscountFromURL() {
    const couponFromPath = getCouponFromPath();

    if (couponFromPath) {
      return saveDiscountCode(couponFromPath);
    }

    const couponFromParams = getCouponFromParams();

    if (couponFromParams) {
      return saveDiscountCode(couponFromParams);
    }

    const couponFromGenericPath = getCouponFromGenericSourcePath();

    if (couponFromGenericPath) {
      return saveDiscountCode(couponFromGenericPath);
    }

    return null;
  }

  function applyDiscountToCart(code) {
    code = normalizeCode(code);

    if (!code) return Promise.resolve(null);
    if (isProbablyUtmCampaignCode(code)) return Promise.resolve(null);

    return fetch(
      buildUrl('/discount/' + encodeURIComponent(code)) +
        '?redirect=' +
        encodeURIComponent(buildUrl('/cart.js')),
      {
        method: 'GET',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json, text/plain, */*'
        },
        cache: 'no-store'
      }
    )
      .catch(function () {
        return null;
      })
      .then(function () {
        return fetch(buildUrl('/cart/update.js'), {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            discount: code,
            attributes: { discount_code: code }
          })
        });
      })
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        window.dispatchEvent(
          new CustomEvent('ne:discount-applied-to-cart', {
            detail: { code: code, cart: cart }
          })
        );

        document.dispatchEvent(
          new CustomEvent('discount:updated', {
            detail: { code: code, cart: cart }
          })
        );

        document.dispatchEvent(
          new CustomEvent('quick-cart:discount-updated', {
            detail: { code: code, cart: cart }
          })
        );

        return cart;
      })
      .catch(function (error) {
        console.error('Error applying discount to cart:', error);
        return null;
      });
  }

  async function fetchProductDiscount(code, productId) {
    if (!code || !productId) return null;

    code = normalizeCode(code);
    productId = String(productId).trim();

    if (!code || !productId) return null;
    if (isProbablyUtmCampaignCode(code)) return null;

    const productCacheKey = productId;
    const memoryCacheKey = code + ':' + productCacheKey;
    const storageCacheKey = PRODUCT_DISCOUNT_CACHE_PREFIX + code;

    window.NE = window.NE || {};
    window.NE.discountProductMemoryCache = window.NE.discountProductMemoryCache || {};
    window.NE.discountProductPendingRequests =
      window.NE.discountProductPendingRequests || {};

    const memoryCached = window.NE.discountProductMemoryCache[memoryCacheKey];

    if (
      memoryCached &&
      memoryCached.timestamp &&
      Date.now() - memoryCached.timestamp < PRODUCT_DISCOUNT_CACHE_TTL
    ) {
      return memoryCached.percentage;
    }

    try {
      const storedCache = sessionStorage.getItem(storageCacheKey);

      if (storedCache) {
        const parsedCache = JSON.parse(storedCache);
        const cachedProduct = parsedCache[productCacheKey];

        if (
          cachedProduct &&
          cachedProduct.timestamp &&
          Date.now() - cachedProduct.timestamp < PRODUCT_DISCOUNT_CACHE_TTL
        ) {
          window.NE.discountProductMemoryCache[memoryCacheKey] = cachedProduct;
          return cachedProduct.percentage;
        }
      }
    } catch (error) {}

    if (window.NE.discountProductPendingRequests[memoryCacheKey]) {
      return window.NE.discountProductPendingRequests[memoryCacheKey];
    }

    const request = (async function() {
      try {
        const response = await fetch(
          buildUrl('/apps/ne-discounts/product-discount') +
            '?code=' +
            encodeURIComponent(code) +
            '&productId=' +
            encodeURIComponent(productId),
          {
            method: 'GET',
            credentials: 'same-origin',
            headers: {
              Accept: 'application/json'
            },
            cache: 'no-store'
          }
        );

        if (!response.ok) {
          console.warn('Failed to fetch product discount:', response.status);
          return null;
        }

        const data = await response.json();

        const productWasExplicitlyRejected = Boolean(
          data &&
            (
              data.eligible === false ||
              data.productIdFound === false ||
              (data.debug && data.debug.productIdFound === false)
            )
        );

        const percentage =
          data && data.success && data.percentage && !productWasExplicitlyRejected
            ? Number(data.percentage)
            : 0;

        const cacheItem = {
          percentage: percentage,
          timestamp: Date.now()
        };

        window.NE.discountProductMemoryCache[memoryCacheKey] = cacheItem;

        try {
          const existingCacheRaw = sessionStorage.getItem(storageCacheKey);
          const existingCache = existingCacheRaw ? JSON.parse(existingCacheRaw) : {};

          existingCache[productCacheKey] = cacheItem;

          sessionStorage.setItem(storageCacheKey, JSON.stringify(existingCache));
        } catch (error) {}

        return percentage;
      } catch (error) {
        console.error('Error fetching product discount:', error);
        return null;
      }
    })();

    window.NE.discountProductPendingRequests[memoryCacheKey] = request;

    try {
      return await request;
    } finally {
      if (window.NE.discountProductPendingRequests[memoryCacheKey] === request) {
        delete window.NE.discountProductPendingRequests[memoryCacheKey];
      }
    }
  }

  function saveDiscountDetails(details) {
    if (!details || !details.code) return;

    details.code = normalizeCode(details.code);

    if (isProbablyUtmCampaignCode(details.code)) return;

    try {
      sessionStorage.setItem(DETAILS_STORAGE_KEY, JSON.stringify(details));

      window.dispatchEvent(
        new CustomEvent('ne:discount-details-updated', {
          detail: details
        })
      );
    } catch (error) {
      console.error('Error saving discount details:', error);
    }
  }

  async function fetchDiscountDetails(code) {
    code = normalizeCode(code);

    if (!code) return null;
    if (isProbablyUtmCampaignCode(code)) return null;

    try {
      const cachedDetails = sessionStorage.getItem(DETAILS_STORAGE_KEY);

      if (cachedDetails) {
        const parsed = JSON.parse(cachedDetails);

        if (
          parsed.code === code &&
          parsed.timestamp &&
          Date.now() - parsed.timestamp < 60000
        ) {
          return parsed;
        }
      }
    } catch (error) {}

    try {
      const response = await fetch(buildUrl('/cart.js') + '?t=' + Date.now(), {
        method: 'GET',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json'
        },
        cache: 'no-store'
      });

      const cart = await response.json();

      if (
        cart.cart_level_discount_applications &&
        cart.cart_level_discount_applications.length > 0
      ) {
        const discount = cart.cart_level_discount_applications.find(function (d) {
          return String(d.title).toUpperCase() === code;
        });

        if (discount) {
          const details = {
            code: code,
            percentage:
              discount.value_type === 'percentage'
                ? Math.round(Number(discount.value))
                : null,
            value: discount.total_allocated_amount,
            type: discount.value_type,
            displayText: 'Code ' + code + ' applied',
            timestamp: Date.now()
          };

          saveDiscountDetails(details);
          return details;
        }
      }

      const urlParams = new URLSearchParams(window.location.search || '');
      const percentageHint = urlParams.get('percentage');

      if (percentageHint) {
        const details = {
          code: code,
          percentage: parseInt(percentageHint, 10),
          displayText: 'Code ' + code + ' applied',
          timestamp: Date.now()
        };

        saveDiscountDetails(details);
        return details;
      }

      const details = {
        code: code,
        percentage: null,
        displayText: 'Code ' + code + ' applied',
        timestamp: Date.now()
      };

      saveDiscountDetails(details);
      return details;
    } catch (error) {
      console.error('Error fetching discount details:', error);

      const fallbackDetails = {
        code: code,
        percentage: null,
        displayText: 'Code ' + code + ' applied',
        timestamp: Date.now()
      };

      saveDiscountDetails(fallbackDetails);
      return fallbackDetails;
    }
  }

  function getDiscountDetails() {
    const code = getDiscountCode();

    if (!code) return null;
    if (isProbablyUtmCampaignCode(code)) return null;

    try {
      const cachedDetails = sessionStorage.getItem(DETAILS_STORAGE_KEY);

      if (cachedDetails) {
        const parsed = JSON.parse(cachedDetails);

        if (parsed && normalizeCode(parsed.code) === code) {
          return parsed;
        }
      }
    } catch (error) {}

    return {
      code: code,
      percentage: null,
      displayText: 'Code ' + code + ' applied'
    };
  }

  function refreshDiscountState(code) {
    code = normalizeCode(code);

    if (!code) return;
    if (isProbablyUtmCampaignCode(code)) {
      clearDiscountCode();
      return;
    }

    window.dispatchEvent(
      new CustomEvent('ne:discount-code-updated', {
        detail: { code: code }
      })
    );

    applyDiscountToCart(code)
      .then(function () {
        return fetchDiscountDetails(code);
      })
      .then(function (details) {
        if (details) {
          saveDiscountDetails(details);
        }
      })
      .catch(function (error) {
        console.warn('Error refreshing discount details:', error);
      });
  }

  function forceCorrectCouponFromUrlEarly() {
    const urlCoupon = getCouponFromPath() || getCouponFromParams();

    if (!urlCoupon) return null;

    try {
      sessionStorage.setItem(STORAGE_KEY, urlCoupon);
      sessionStorage.setItem('ne_vanity_discount', urlCoupon);
      sessionStorage.removeItem(DETAILS_STORAGE_KEY);
    } catch (error) {}

    setCookie(COOKIE_NAME, urlCoupon, COOKIE_DAYS);

    return urlCoupon;
  }

  function init() {
    /*
      This runs before stored values are reused.
      It fixes old bad values like BRAND_WEEK_... immediately.
    */
    const forcedUrlCoupon = forceCorrectCouponFromUrlEarly();

    const urlDiscount = detectDiscountFromURL();
    let activeCode = urlDiscount || forcedUrlCoupon;

    if (!activeCode) {
      const storedDiscount = getDiscountCode();

      if (storedDiscount) {
        activeCode = storedDiscount;
      }
    }

    if (activeCode) {
      refreshDiscountState(activeCode);
    }

    document.addEventListener('cart:opened', function () {
      const discountCode = getDiscountCode();

      if (discountCode) {
        refreshDiscountState(discountCode);
      }
    });

    document.addEventListener('quick-cart:open', function () {
      const discountCode = getDiscountCode();

      if (discountCode) {
        refreshDiscountState(discountCode);
      }
    });

    document.addEventListener('cart:updated', function () {
      const discountCode = getDiscountCode();

      if (discountCode) {
        fetchDiscountDetails(discountCode)
          .then(function (details) {
            if (details) {
              saveDiscountDetails(details);
            }
          })
          .catch(function (error) {
            console.warn('Error updating discount details on cart update:', error);
          });
      }
    });

    window.addEventListener('popstate', function () {
      const newCode = detectDiscountFromURL();

      if (newCode) {
        refreshDiscountState(newCode);
      }
    });
  }

  /*
    Run this immediately, not only on DOMContentLoaded.
    This prevents the toast from reading old BRAND_WEEK... before we fix it.
  */
  forceCorrectCouponFromUrlEarly();

  window.NE = window.NE || {};

  window.NE.DiscountTracker = {
    save: saveDiscountCode,
    get: getDiscountCode,
    clear: clearDiscountCode,
    getDetails: getDiscountDetails,
    fetchDetails: fetchDiscountDetails,
    fetchProductDiscount: fetchProductDiscount,
    applyToCart: applyDiscountToCart,
    refresh: refreshDiscountState,
    clearProductDiscountCache: clearProductDiscountCache
  };

  /*
    The discount UI snippets are rendered later in the document and read the
    tracker immediately. Initialise here so code detection and storage are
    ready before badge and price pipelines start.
  */
  init();
})();
